import { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import authAPI from '../api/authAPI';
import { GlobalContext } from '../context/provider';

const useAuth = () => {
  const navigate = useNavigate();
  const { authDispatch } = useContext(GlobalContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // ============================================================================
  // SESSION & LOGIN
  // ============================================================================

  // const checkSession = useCallback(async () => {
  //   setIsLoading(true);
  //   setError(null);
  //   try {
  //     const response = await authAPI.checkSession();
  //     setIsLoading(false);
  //     return response;
  //   } catch (err) {
  //     console.error('Session check error:', err);
  //     setError(err.message);
  //     setIsLoading(false);
  //     throw err;
  //   }
  // }, [authDispatch]);

  const checkSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authAPI.checkSession();
      setIsLoading(false);
      
      if (response.authenticated && response.user) {
        const payload = {
          email: response.user.email,
          id: response.user.id,
          name: response.user.name,
          photo: response.user.photo || response.user.picture,
          role: response.user.role || 'student'
        };
        
        authDispatch({
          type: 'LOGIN_SUCCESS',
          payload
        });
        
        localStorage.setItem('userInfo', JSON.stringify(payload));
      }
      
      return response;
    } catch (err) {
      console.error('Session check error:', err);
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, [authDispatch]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authAPI.logout();
      
      if (response.success) {
        authDispatch({ type: 'LOGOUT', payload: '' });
        localStorage.removeItem('userRole');
        localStorage.removeItem('userInfo');
        sessionStorage.clear();
        navigate('/');
      }
      
      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, [authDispatch, navigate]);

  const initiateGoogleLogin = useCallback(() => {
    authAPI.initiateGoogleLogin();
  }, []);

  const initiateMicrosoftLogin = useCallback(() => {
    authAPI.initiateMicrosoftLogin();
  }, []);

  const initiateGitHubLogin = useCallback(() => {
    authAPI.initiateGitHubLogin();
  }, []);

  // ============================================================================
  // TWO-FACTOR AUTHENTICATION
  // ============================================================================

  const generate2FASecret = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authAPI.generate2FASecret();
      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('Generate 2FA secret error:', err);
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, []);

  const enable2FA = useCallback(async (secret, token) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authAPI.enable2FA(secret, token);
      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('Enable 2FA error:', err);
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, []);

  const verify2FA = useCallback(async (token) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authAPI.verify2FA(token);
      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('Verify 2FA error:', err);
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, []);

  const disable2FA = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authAPI.disable2FA();
      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('Disable 2FA error:', err);
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, []);

  const check2FAStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authAPI.check2FAStatus();
      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('Check 2FA status error:', err);
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, []);

  return {
    // Session & Login
    checkSession,
    logout,
    initiateGoogleLogin,
    initiateMicrosoftLogin,
    initiateGitHubLogin,
    
    // Two-Factor Authentication
    generate2FASecret,
    enable2FA,
    verify2FA,
    disable2FA,
    check2FAStatus,
    
    // State
    isLoading,
    error
  };
};

export default useAuth;